import { useMemo } from "react";
import { sankey as d3sankey, sankeyLinkHorizontal, sankeyLeft } from "d3-sankey";

/**
 * Premium interactive Sankey renderer for VAULT Cash Flow.
 *
 * Props:
 *   data       — { nodes: [{id,name,color,tier,kind}], links: [{source,target,value,color}] }
 *   height     — px (default 480)
 *   width      — px (default responsive via container)
 *   hideValues — when true, render "•••" instead of amounts (Privacy Shield)
 *   currency   — symbol prefix ("₹", "$", …)
 *   onNodeClick, onLinkClick — optional drill-down handlers
 */
export default function CashFlowSankey({
    data,
    width = 960,
    height = 480,
    hideValues = false,
    currency = "₹",
    onNodeClick,
    onLinkClick,
}) {
    const layout = useMemo(() => {
        if (!data?.nodes?.length || !data?.links?.length) return null;
        // Deep-clone so d3-sankey doesn't mutate our props.
        const nodes = data.nodes.map((n) => ({ ...n }));
        const links = data.links.map((l) => ({ ...l }));
        const gen = d3sankey()
            .nodeId((d) => d.id)
            .nodeAlign(sankeyLeft)
            .nodeWidth(18)
            .nodePadding(16)
            .extent([[130, 24], [width - 220, height - 24]]);
        return gen({ nodes, links });
    }, [data, width, height]);

    if (!layout) return null;

    const fmt = (v) => {
        if (hideValues) return "•••";
        if (v >= 10_000_000) return `${currency}${(v / 10_000_000).toFixed(2)} Cr`;
        if (v >= 100_000)    return `${currency}${(v / 100_000).toFixed(2)} L`;
        if (v >= 1_000)      return `${currency}${(v / 1_000).toFixed(1)}k`;
        return `${currency}${Math.round(v)}`;
    };

    const total = layout.links.reduce((s, l) => s + l.value, 0);

    return (
        <svg width="100%" viewBox={`0 0 ${width} ${height}`}
             className="cf-sankey"
             data-testid="cash-flow-sankey">
            <defs>
                {layout.links.map((l, i) => (
                    <linearGradient key={i} id={`cf-grad-${i}`}
                        gradientUnits="userSpaceOnUse"
                        x1={l.source.x1} x2={l.target.x0}>
                        <stop offset="0%" stopColor={l.source.color || l.color} stopOpacity="0.72" />
                        <stop offset="100%" stopColor={l.target.color || l.color} stopOpacity="0.72" />
                    </linearGradient>
                ))}
            </defs>

            {/* Links */}
            <g fill="none" strokeOpacity="0.55">
                {layout.links.map((l, i) => (
                    <path
                        key={i}
                        d={sankeyLinkHorizontal()(l)}
                        stroke={`url(#cf-grad-${i})`}
                        strokeWidth={Math.max(1, l.width)}
                        className="cf-sankey__link"
                        onClick={() => onLinkClick?.(l)}
                        data-testid={`cf-link-${l.source.name}-${l.target.name}`}
                    >
                        <title>
                            {l.source.name} → {l.target.name}
                            {"\n"}
                            {fmt(l.value)} ({total ? Math.round((l.value / total) * 100) : 0}%)
                        </title>
                    </path>
                ))}
            </g>

            {/* Nodes */}
            <g>
                {layout.nodes.map((n) => (
                    <g key={n.id}
                       className="cf-sankey__node"
                       onClick={() => onNodeClick?.(n)}
                       data-testid={`cf-node-${n.name}`}>
                        <rect x={n.x0} y={n.y0}
                              width={n.x1 - n.x0}
                              height={Math.max(4, n.y1 - n.y0)}
                              fill={n.color}
                              rx={3} />
                        <title>
                            {n.name}
                            {"\n"}
                            {fmt(n.value)}
                        </title>
                    </g>
                ))}
            </g>

            {/* Labels */}
            <g fontSize="12" fontFamily="Inter, system-ui, sans-serif">
                {layout.nodes.map((n) => {
                    const isRight = n.x0 >= width / 2;
                    const pct = total > 0 ? Math.round((n.value / total) * 100) : 0;
                    const anchor = isRight ? "start" : "end";
                    const xPos = isRight ? n.x1 + 8 : n.x0 - 8;
                    return (
                        <g key={`t-${n.id}`}>
                            <text x={xPos} y={(n.y0 + n.y1) / 2 - 6}
                                  textAnchor={anchor} fill="#1c1917"
                                  fontWeight="600"
                                  className="cf-sankey__label">
                                {n.name}
                            </text>
                            <text x={xPos} y={(n.y0 + n.y1) / 2 + 8}
                                  textAnchor={anchor} fill="#78716c" fontSize="11">
                                {fmt(n.value)}{total > 0 ? ` · ${pct}%` : ""}
                            </text>
                        </g>
                    );
                })}
            </g>
        </svg>
    );
}
